import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { SlotSuggestion } from '@shared/types/index.ts';
import { scheduleApi } from '../../api/endpoints.ts';
import { weekdayLabel } from './format.ts';
import { formatLocalTime, formatShortDate } from '../../i18n/datetime.ts';

/**
 * Quick picks: a handful of good slots for the chosen crew, one click each.
 *
 * The honesty is the feature. A suggestion carries how many of the schedule's
 * upcoming visits fit ("all 12 clear", "11 of 12"), because a recurring
 * booking that is perfect for twelve weeks is rare and not worth holding out
 * for — the note under the chips says so in words, and booking an imperfect
 * one is explicitly fine: the odd clashing visit is left out and listed for
 * rescheduling, the rest book normally.
 *
 * The same honesty covers a job too long for the working day — a nine-hour
 * deep clean quoted against a nine-to-five, which is what an accepted quote
 * often hands this form. Its picks all start at opening time and all finish
 * after closing, so the note below them says so rather than letting the
 * office read "09:00" as a slot that fits.
 *
 * And when there are no picks at all, the block stays put and says why. An
 * empty list has several deliberate causes — the fortnight is genuinely
 * booked out, the busy set was too big to score, the crew's calendar cache
 * does not reach that far — and vanishing made them indistinguishable from
 * each other and from a feature that was never here. The one case still
 * rendered as nothing is a failed request, which the page reports elsewhere.
 */
export function SlotSuggestions({
  userIds,
  teamId,
  durationMinutes,
  frequency,
  isSelected,
  onPick,
}: {
  userIds: string[];
  teamId?: string | null;
  durationMinutes: number;
  frequency: string;
  isSelected?: (suggestion: SlotSuggestion) => boolean;
  onPick: (suggestion: SlotSuggestion) => void;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['schedule-suggestions', teamId, userIds.join(','), durationMinutes, frequency],
    queryFn: () => scheduleApi.suggestions({ userIds, teamId, durationMinutes, frequency }),
    enabled: durationMinutes >= 15,
  });

  const suggestions = query.data?.suggestions ?? [];
  const isEmpty = query.isSuccess && suggestions.length === 0;
  if (query.isError) return null;

  return (
    <div className="mb-4">
      <p className="field-label">{t('suggestions.heading')}</p>
      {query.isLoading ? (
        <p className="text-sm text-[color:var(--color-muted)]">{t('suggestions.loading')}</p>
      ) : isEmpty ? (
        /* Why there is nothing, and where to go instead. The grid below books
           any hour on a click, so an empty quick-picks list is a missing
           shortcut rather than a closed door — worth saying in the same
           breath as the reason. */
        <p className="field-hint">
          {t(`suggestions.empty.${query.data?.emptyReason ?? 'all_taken'}`)} {t('suggestions.empty.gridNote')}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => {
              const clean = suggestion.occurrencesClashing === 0;
              const selected = isSelected?.(suggestion) ?? false;
              return (
                <button
                  key={`${suggestion.startsOn}-${suggestion.startTimeLocal}`}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onPick(suggestion)}
                  className={
                    'rounded-lg border px-3 py-2 text-left transition-colors ' +
                    (selected
                      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                      : 'border-[color:var(--color-line)] hover:border-[color:var(--color-brand)] hover:bg-[color:var(--color-brand-soft)]')
                  }
                >
                  <span className="block text-sm font-semibold">
                    {formatShortDate(suggestion.startsOn)} · {formatLocalTime(suggestion.startTimeLocal)}
                  </span>
                  {/* Why this one. A slot offered without a reason is a
                      number the office has to take on trust; "you already
                      work Tuesdays" is something they can agree or disagree
                      with. */}
                  {!suggestion.runsPastClosing && !suggestion.availabilityUncertain && (
                    <span className="block text-xs text-[color:var(--color-muted)]">
                      {t(`suggestions.reason.${suggestion.reason}`, {
                        nth: t(`schedule.nthWeek.${suggestion.nthWeek ?? 1}`).toLowerCase(),
                        day: weekdayLabel(suggestion.dayOfWeek),
                      })}
                    </span>
                  )}
                  {suggestion.runsPastClosing ? (
                    <span className="block text-xs text-[color:var(--color-warning)]">
                      {t('planning.outOfHoursPick')}
                    </span>
                  ) : null}
                  {suggestion.availabilityUncertain ? (
                    <span className="block text-xs text-[color:var(--color-warning)]">{t('planning.uncertain')}</span>
                  ) : null}
                  {suggestion.occurrencesChecked > 1 &&
                  !suggestion.runsPastClosing &&
                  !suggestion.availabilityUncertain ? (
                    <span
                      className={
                        'block text-xs ' +
                        (clean ? 'text-[color:var(--color-brand-strong)]' : 'text-[color:var(--color-warning)]')
                      }
                    >
                      {clean
                        ? t('suggestions.allClear', { total: suggestion.occurrencesChecked })
                        : t('suggestions.mostlyClear', {
                            clear: suggestion.occurrencesChecked - suggestion.occurrencesClashing,
                            total: suggestion.occurrencesChecked,
                          })}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          {query.data?.runsPastClosing ? <p className="field-hint mt-2">{t('suggestions.pastClosingNote')}</p> : null}
          <p className="field-hint mt-2">{t('suggestions.toleranceNote')}</p>
        </>
      )}
    </div>
  );
}
